import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Close as CloseIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as DuplicateIcon,
  Description as FileIcon
} from '@mui/icons-material';
import api from '../api/api'; // Ajusta la ruta según la carpeta de tu instancia Axios

// Función auxiliar para extraer y decodificar el payload del Token JWT
const getDatosUsuarioDesdeToken = () => {
  try {
    const token = localStorage.getItem('token');
    if (!token) return null;

    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error("Error al decodificar el token JWT:", error);
    return null;
  }
};

export const ImportarDteModal = ({ open, onClose, idSucursal, idPet = null, idCliente = null, nombreCliente = '', onSuccess }) => {
  
  const [loading, setLoading] = useState(false);
  const [archivos, setArchivos] = useState([]);
  const [resultados, setResultados] = useState([]);
  const [progreso, setProgreso] = useState(0);

  // Extraer códigos QR de pacientes (PAC-XXXXXX)
  const buscarQr = (data) => {
    const texto = JSON.stringify(data);
    const matches = texto.match(/PAC-\d{6}/gi);
    return matches ? Array.from(new Set(matches.map(m => m.toUpperCase()))) : [];
  };

  // Mapear JSON DTE al Payload esperado por la API
  const mapearDteAPayload = (data) => {
    if (!data?.identificacion?.codigoGeneracion) {
      throw new Error("El archivo no contiene un DTE o 'codigoGeneracion' válido.");
    }

    // Extraer valores reales directamente desde el Token JWT activo
    const userToken = getDatosUsuarioDesdeToken();
    const idPetDinamico = userToken?.id_pet || idPet;
    const idSucursalDinamica = userToken?.id_sucursal || idSucursal;

    const identificacion = data.identificacion;
    const emisor = data.emisor || {};
    const receptor = data.receptor || data.sujetoExcluido || data.donatario || {};
    const resumen = data.resumen || {};

    const tributos = resumen.tributos || [];
    const tributoIva = tributos.find(t => t.codigo === "20" || t.codigo === "C2");
    const ivaVal = tributoIva ? parseFloat(tributoIva.valor) : 0;

    return {
      id_pet: idPetDinamico,
      id_sucursal: idSucursalDinamica,
      id_cliente: idCliente,
      codigo_generacion: identificacion.codigoGeneracion,
      numero_control: identificacion.numeroControl,
      sello_recibido: data.selloRecibido || "",
      tipo_dte: identificacion.tipoDte,
      tipo_establecimiento: emisor.tipoEstablecimiento || null,
      fecha_emision: identificacion.fecEmi,
      hora_emision: identificacion.horEmi || null,
      direccion_emisor: emisor.direccion?.complemento || "",
      nit_emisor: emisor.nit || "",
      nombre_emisor: emisor.nombre || "",
      nit_receptor: receptor.nit || null,
      nrc_receptor: receptor.nrc || null,
      dui_receptor: receptor.tipoDocumento === "13" ? receptor.numDocumento : null,
      pasaporte_receptor: receptor.tipoDocumento === "03" ? receptor.numDocumento : null,
      carnet_residente_receptor: receptor.tipoDocumento === "02" ? receptor.numDocumento : null,
      nombre_receptor: receptor.nombre || null,
      estado_vinculacion: "VINCULADO_MANUAL",
      nota_aclaratoria: idCliente 
        ? `Vinculado manualmente vía ${nombreCliente} (ID: ${idCliente})`
        : null,
      monto_total_operacion: parseFloat(resumen.montoTotalOperacion || resumen.totalPagar || 0),
      total_gravada: parseFloat(resumen.totalGravada || 0),
      total_exenta: parseFloat(resumen.totalExenta || 0),
      total_no_suj: parseFloat(resumen.totalNoSuj || 0),
      iva: ivaVal,
      retencion_iva: parseFloat(resumen.ivaRete1 || resumen.reteRenta || 0),
      monto_total_pagar: parseFloat(resumen.totalPagar || resumen.montoTotalOperacion || 0),
      codigos_qr_paciente: buscarQr(data),
      json_completo: data,
      pdf_url: "",
      fuente_importacion: "importador_modal_web"
    };
  };

  // Selección de archivos en el input
    const handleFileChange = (e) => {
      const selectedFiles = Array.from(e.target.files).filter(file => file.name.endsWith('.json'));
      setArchivos(selectedFiles);
      setResultados([]);
    };

// Procesar e importar los archivos
  const handleProcesar = async () => {
    if (archivos.length === 0) return;

    setLoading(true);
    setProgreso(0);
    const nuevosResultados = [];
    let ultimoNumeroControl = null;
    let ultimoCodigoGeneracion = null;

    for (let i = 0; i < archivos.length; i++) {
      const file = archivos[i];
      let json = null;

      try {
        const contenido = await file.text();
        json = JSON.parse(contenido);

        // 1. EXTRAER DATOS DIRECTAMENTE DEL JSON LOCAL
        const numControlLocal = json?.identificacion?.numeroControl;
        const codGenLocal = json?.identificacion?.codigoGeneracion;

        const payload = mapearDteAPayload(json);

        // Envío a la API
        await api.post('/documentos_fiscales/', payload);

        ultimoNumeroControl = payload.numero_control || numControlLocal;
        ultimoCodigoGeneracion = payload.codigo_generacion || codGenLocal;

        nuevosResultados.push({
          nombre: file.name,
          estado: 'exito',
          mensaje: 'Importado correctamente'
        });
      } catch (err) {
        // 2. RESCATAR SIEMPRE LOS DATOS EN CASO DE ERROR DE LA API
        ultimoNumeroControl = json?.identificacion?.numeroControl || err.response?.data?.numeroControl;
        ultimoCodigoGeneracion = json?.identificacion?.codigoGeneracion || err.response?.data?.codigoGeneracion;

        const errText = err.response?.data?.detail || err.message || "";
        const esDuplicado = err.response?.status === 400 && (
          errText.toLowerCase().includes('duplicado') || 
          errText.toLowerCase().includes('uniqueviolation')
        );

        if (esDuplicado) {
          nuevosResultados.push({
            nombre: file.name,
            estado: 'duplicado',
            mensaje: 'El documento ya está registrado en la base de datos'
          });
        } else {
          nuevosResultados.push({
            nombre: file.name,
            estado: 'error',
            mensaje: typeof errText === 'string' ? errText : 'Error al procesar el archivo'
          });
        }
      }

      setProgreso(Math.round(((i + 1) / archivos.length) * 100));
    }

    setResultados(nuevosResultados);
    setLoading(false);

    // 3. EJECUTAR EL CALLBACK AUNQUE HAYA SIDO DUPLICADO EN BD GENERAL
    if ((ultimoNumeroControl || ultimoCodigoGeneracion) && onSuccess) {
      onSuccess(ultimoNumeroControl, ultimoCodigoGeneracion);
    }
  };

  const handleReset = () => {
    setArchivos([]);
    setResultados([]);
    setProgreso(0);
  };

  const handleCloseModal = () => {
    if (!loading) {
      handleReset();
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <UploadIcon color="primary" />
          <Typography variant="h6" fontWeight="bold">
            Importar Documentos Electrónicos (JSON)
          </Typography>
        </Box>
        <IconButton onClick={handleCloseModal} disabled={loading}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {/* Zona de Drop / Carga de archivos */}
          {archivos.length === 0 && (
            <Paper
              variant="outlined"
              component="label"
              sx={{
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                borderStyle: 'dashed',
                borderWidth: 2,
                borderColor: 'primary.main',
                borderRadius: 2,
                bgcolor: 'background.paper',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  bgcolor: 'action.hover',
                  borderColor: 'primary.dark',
                },
              }}
            >
              <input
                type="file"
                multiple
                accept=".json"
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <UploadIcon sx={{ fontSize: 52, color: 'primary.main', mb: 1.5 }} />
              <Typography variant="h6" color="text.primary" fontWeight="bold" align="center">
                Haz clic para seleccionar archivos JSON DTE
              </Typography>
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 0.5 }}>
                Puedes seleccionar múltiples comprobantes electrónicos (.json) a la vez.
              </Typography>
            </Paper>
          )}

        {/* Lista de archivos seleccionados antes de procesar */}
        {archivos.length > 0 && resultados.length === 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Archivos seleccionados ({archivos.length}):
            </Typography>
            <Paper variant="outlined" sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              <List dense>
                {archivos.map((file, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon><FileIcon color="action" /></ListItemIcon>
                    <ListItemText primary={file.name} secondary={`${(file.size / 1024).toFixed(1)} KB`} />
                  </ListItem>
                ))}
              </List>
            </Paper>

            {loading && (
              <Box sx={{ my: 2 }}>
                <Typography variant="caption" color="textSecondary">
                  Procesando importación... {progreso}%
                </Typography>
                <LinearProgress variant="determinate" value={progreso} sx={{ mt: 1 }} />
              </Box>
            )}
          </Box>
        )}

        {/* Resultados del procesamiento */}
        {resultados.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Resumen de la Importación:
            </Typography>
            <Paper variant="outlined" sx={{ maxHeight: 250, overflow: 'auto' }}>
              <List dense>
                {resultados.map((res, idx) => (
                  <ListItem key={idx}>
                    <ListItemIcon>
                      {res.estado === 'exito' && <SuccessIcon color="success" />}
                      {res.estado === 'duplicado' && <DuplicateIcon color="warning" />}
                      {res.estado === 'error' && <ErrorIcon color="error" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={res.nombre}
                      secondary={res.mensaje}
                      secondaryTypographyProps={{
                        color: res.estado === 'exito' ? 'success.main' : res.estado === 'duplicado' ? 'warning.main' : 'error.main'
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        {resultados.length > 0 ? (
          <Button onClick={handleReset} variant="outlined">
            Cargar Más Archivos
          </Button>
        ) : (
          <Button onClick={handleCloseModal} disabled={loading}>
            Cancelar
          </Button>
        )}

        {archivos.length > 0 && resultados.length === 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleProcesar}
            disabled={loading}
            startIcon={<UploadIcon />}
          >
            {loading ? 'Procesando...' : `Importar ${archivos.length} Archivo(s)`}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ImportarDteModal;